import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface LineChartWidgetProps {
  config: {
    chartType?: string;
    dateRange?: '7d' | '30d' | '90d' | '180d' | '365d' | 'custom';
    startDate?: string;
    endDate?: string;
  };
  size: WidgetSize;
}

const LineChartWidget: React.FC<LineChartWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    loadChartData();
  }, [config.chartType, config.dateRange, config.startDate, config.endDate, userProfile?.empresaCnpj]);

  const getDateRange = () => {
    const endDate = config.endDate ? new Date(config.endDate) : new Date();
    let startDate = new Date();

    if (config.dateRange === 'custom' && config.startDate) {
      startDate = new Date(config.startDate);
    } else {
      const days = config.dateRange === '7d' ? 7 :
                   config.dateRange === '30d' ? 30 :
                   config.dateRange === '90d' ? 90 :
                   config.dateRange === '180d' ? 180 :
                   config.dateRange === '365d' ? 365 : 30;
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - days);
    }

    return { startDate, endDate };
  };

  const loadChartData = async () => {
    if (!userProfile?.empresaCnpj || !config.chartType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      const charts = await firebaseService.getDashboardCharts(userProfile.empresaCnpj);

      // Filtra dados pelo período selecionado
      let chartData = charts.chartData || [];
      if (chartData.length > 0 && chartData[0].mes) {
        chartData = chartData.filter((item: any) => {
          // Assumindo que 'mes' está no formato 'MM/YYYY' ou similar
          // Ajuste conforme o formato real dos dados
          return true; // Por enquanto retorna todos, ajustar conforme necessário
        });
      }

      switch (config.chartType) {
        case 'receita-vs-alunos':
          setTitle('Receita vs Alunos');
          setData(chartData);
          break;
        case 'evolucao-aprovacoes':
          setTitle('Evolução de Aprovações');
          // Implementar lógica de avaliações com filtro de data
          setData([]);
          break;
        case 'evolucao-receita':
          setTitle('Evolução de Receita');
          setData(chartData);
          break;
        case 'evolucao-alunos':
          setTitle('Evolução de Alunos');
          setData(chartData);
          break;
        default:
          setTitle('Gráfico de Linha');
          setData(chartData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {title && (
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex-shrink-0">{title}</h3>
      )}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="mes" stroke="#6b7280" fontSize={12} />
          <YAxis yAxisId="left" stroke="#6b7280" fontSize={12} />
          <YAxis yAxisId="right" orientation="right" stroke="#6b7280" fontSize={12} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="receita"
            stroke="#3b82f6"
            strokeWidth={2}
            name="Receita (R$)"
            dot={{ r: 4 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="alunos"
            stroke="#22c55e"
            strokeWidth={2}
            name="Alunos"
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChartWidget;

